import { useState, useEffect, useCallback, useRef } from 'react';

export type ConnectivityStatus = 'online' | 'degraded' | 'offline';

interface ConnectivityState {
  status: ConnectivityStatus;
  isOffline: boolean;
  isDegraded: boolean;
  isOnline: boolean;
  latencyMs: number | null;
  lastCheckedAt: Date | null;
  lastOnlineAt: Date | null;
  failureCount: number;
  isInitializing: boolean;
}

interface UseConnectivityOptions {
  /** Timeout for each probe request in ms */
  timeout?: number;
  /** Number of retries before considering a check failed */
  retries?: number;
  /** Consecutive failures required before marking offline */
  offlineThreshold?: number;
  /** Latency threshold for "degraded" status in ms */
  degradedLatencyMs?: number;
  /** Check interval when online (ms) */
  onlineCheckInterval?: number;
  /** Check interval when degraded (ms) */
  degradedCheckInterval?: number;
  /** Check interval when offline (ms) */
  offlineCheckInterval?: number;
}

const DEFAULT_OPTIONS: Required<UseConnectivityOptions> = {
  timeout: 2500,
  retries: 2,
  offlineThreshold: 4,
  degradedLatencyMs: 1500,
  onlineCheckInterval: 30000,
  degradedCheckInterval: 10000,
  offlineCheckInterval: 5000,
};

/**
 * Robust connectivity hook that verifies actual network connectivity
 * using real HTTP requests with retries and debouncing.
 */
export function useConnectivity(options: UseConnectivityOptions = {}): ConnectivityState & {
  recheckNow: () => Promise<void>;
} {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  
  const [state, setState] = useState<ConnectivityState>({
    status: 'online',
    isOffline: false,
    isDegraded: false,
    isOnline: true,
    latencyMs: null,
    lastCheckedAt: null,
    lastOnlineAt: null,
    failureCount: 0,
    isInitializing: true,
  });

  // Use refs to avoid dependency issues and race conditions
  const INITIAL_LOAD_DELAY_MS = 5000; // Wait before first check (increased from 3s)
  const REQUIRED_SUCCESS_COUNT = 2; // Require 2 successes before confirmed online
  
  // Detect slow connection type and adjust grace period dynamically
  const getInitialGracePeriod = (): number => {
    const connection = (navigator as any).connection;
    const effectiveType = connection?.effectiveType;
    const saveData = connection?.saveData === true;
    
    // Longer grace period for slow connections or data saver mode
    if (saveData || effectiveType === '2g' || effectiveType === 'slow-2g') {
      return 45000; // 45 seconds for very slow connections
    }
    if (effectiveType === '3g') {
      return 35000; // 35 seconds for 3G
    }
    return 30000; // 30 seconds default (increased from 20s)
  };
  
  const mountedAtRef = useRef<number>(Date.now());
  const failureCountRef = useRef(0);
  const successCountRef = useRef(0);
  const checkingRef = useRef(false);
  const mountedRef = useRef(true);
  const hasCompletedInitialCheckRef = useRef(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const currentStatusRef = useRef<ConnectivityStatus>('online');
  const initialGracePeriodRef = useRef<number>(getInitialGracePeriod());

  /**
   * Perform a single probe with timeout
   */
  const probe = useCallback(async (): Promise<{ ok: boolean; latencyMs: number; aborted?: boolean }> => {
    if (!navigator.onLine) {
      return { ok: false, latencyMs: 0, aborted: false };
    }
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), opts.timeout);
    
    const start = performance.now();
    
    try {
      const response = await fetch(`/ping.txt?_=${Date.now()}`, {
        method: 'GET',
        cache: 'no-store',
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      const latencyMs = performance.now() - start;
      
      return { ok: response.ok, latencyMs };
    } catch (error) {
      clearTimeout(timeoutId);
      const isAborted = error instanceof DOMException && error.name === 'AbortError';
      return { ok: false, latencyMs: performance.now() - start, aborted: isAborted };
    }
  }, [opts.timeout]);

  /**
   * Perform connectivity check with retries
   */
  const checkConnectivity = useCallback(async (): Promise<void> => {
    if (checkingRef.current || !mountedRef.current) return;
    checkingRef.current = true;

    let success = false;
    let latencyMs = 0;
    
    for (let attempt = 0; attempt <= opts.retries; attempt++) {
      const result = await probe();
      
      if (result.ok) {
        success = true;
        latencyMs = result.latencyMs;
        break;
      }
      
      if (result.aborted) {
        checkingRef.current = false;
        return;
      }
      
      if (attempt < opts.retries) {
        const delay = Math.min(300 * Math.pow(2, attempt), 1500);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }

    if (!mountedRef.current) {
      checkingRef.current = false;
      return;
    }

    const now = new Date();
    const msSinceMount = Date.now() - mountedAtRef.current;

    if (success) {
      successCountRef.current += 1;
      failureCountRef.current = 0;
      
      // Mark initial check complete after first success
      if (!hasCompletedInitialCheckRef.current) {
        hasCompletedInitialCheckRef.current = true;
      }

      const isDegraded = latencyMs > opts.degradedLatencyMs;
      const newStatus: ConnectivityStatus = isDegraded ? 'degraded' : 'online';
      currentStatusRef.current = newStatus;

      // Only mark as fully initialized after required success count
      const isStillInitializing = successCountRef.current < REQUIRED_SUCCESS_COUNT;

      setState({
        status: newStatus,
        isOffline: false,
        isDegraded,
        isOnline: !isDegraded,
        latencyMs,
        lastCheckedAt: now,
        lastOnlineAt: now,
        failureCount: 0,
        isInitializing: isStillInitializing,
      });
    } else {
      successCountRef.current = 0;
      
      // Don't accumulate failures during grace period unless we've had success before
      const shouldAccumulateFailures =
        hasCompletedInitialCheckRef.current || msSinceMount >= initialGracePeriodRef.current;

      if (shouldAccumulateFailures) {
        failureCountRef.current += 1;
      }

      const shouldMarkOffline = failureCountRef.current >= opts.offlineThreshold;
      
      if (shouldMarkOffline) {
        currentStatusRef.current = 'offline';
      }

      setState(prev => ({
        ...prev,
        status: shouldMarkOffline ? 'offline' : prev.status,
        isOffline: shouldMarkOffline,
        isDegraded: !shouldMarkOffline && prev.status === 'degraded',
        isOnline: !shouldMarkOffline && prev.status === 'online',
        latencyMs: null,
        lastCheckedAt: now,
        failureCount: failureCountRef.current,
        isInitializing: !hasCompletedInitialCheckRef.current,
      }));
    }

    checkingRef.current = false;
  }, [opts.retries, opts.offlineThreshold, opts.degradedLatencyMs, probe]);

  /**
   * Manual recheck function exposed to consumers
   */
  const recheckNow = useCallback(async () => {
    failureCountRef.current = 0;
    await checkConnectivity();
  }, [checkConnectivity]);

  // Set up periodic checks - using refs to avoid re-creating intervals
  useEffect(() => {
    mountedRef.current = true;
    mountedAtRef.current = Date.now();

    // Delayed initial check to avoid false positives during page load
    const initialCheckTimeout = setTimeout(() => {
      checkConnectivity();
    }, INITIAL_LOAD_DELAY_MS);

    // Start periodic checks after initial delay
    const startPeriodicChecks = () => {
      // Clear any existing interval
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      // Get interval based on current status (using ref to avoid stale closure)
      const getInterval = () => {
        const status = currentStatusRef.current;
        if (status === 'offline') return opts.offlineCheckInterval;
        if (status === 'degraded') return opts.degradedCheckInterval;
        return opts.onlineCheckInterval;
      };

      // Check and reschedule periodically
      const scheduleCheck = () => {
        checkConnectivity().then(() => {
          if (mountedRef.current) {
            // Schedule next check based on current status
            intervalRef.current = setTimeout(scheduleCheck, getInterval()) as unknown as ReturnType<typeof setInterval>;
          }
        });
      };

      // Start the first scheduled check
      intervalRef.current = setTimeout(scheduleCheck, getInterval()) as unknown as ReturnType<typeof setInterval>;
    };

    // Start periodic checks after initial check completes
    const periodicStartTimeout = setTimeout(startPeriodicChecks, INITIAL_LOAD_DELAY_MS + 1000);

    // Browser online/offline events as signals
    const handleOnline = () => {
      // Reset grace period on coming back online
      mountedAtRef.current = Date.now();
      successCountRef.current = 0;
      checkConnectivity();
    };

    const handleOffline = () => {
      checkConnectivity();
    };

    // Recheck on visibility change (user returns to tab)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Reset grace period when user returns to tab
        mountedAtRef.current = Date.now();
        successCountRef.current = 0;
        failureCountRef.current = 0;
        hasCompletedInitialCheckRef.current = false;
        setState(prev => ({ ...prev, isInitializing: true }));
        checkConnectivity();
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      mountedRef.current = false;
      clearTimeout(initialCheckTimeout);
      clearTimeout(periodicStartTimeout);
      if (intervalRef.current) {
        clearTimeout(intervalRef.current as unknown as number);
        clearInterval(intervalRef.current);
      }
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkConnectivity, opts.onlineCheckInterval, opts.degradedCheckInterval, opts.offlineCheckInterval]);

  return {
    ...state,
    recheckNow,
  };
}
